function toArrayValue(value: unknown) {
  if (Array.isArray(value)) return value.map((item) => String(item));
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

type MedicalFormField = {
  id: string;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'date'
    | 'datetime'
    | 'time'
    | 'email'
    | 'phone'
    | 'url'
    | 'select'
    | 'multiselect'
    | 'radio'
    | 'checkbox'
    | 'switch'
    | 'instruction'
    | 'rating'
    | 'scale'
    | 'signature'
    | 'file';
  label: string;
  helpText?: string;
  placeholder?: string;
  required?: boolean;
  options?: Array<{ label: string; value: string }>;
  validation?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    step?: number;
  };
  conditional?: {
    fieldId: string;
    operator: 'equals' | 'not_equals' | 'includes' | 'not_includes' | 'greater_than' | 'less_than';
    value: unknown;
  };
  layout?: {
    width?: 'full' | 'half';
  };
};

type MedicalFormDefinition = {
  version: number;
  introText?: string;
  sections: Array<{
    id: string;
    title: string;
    description?: string;
    fields: MedicalFormField[];
  }>;
};

function isVisible(field: MedicalFormField, answers: Record<string, unknown>) {
  if (!field.conditional) return true;
  const left = answers[field.conditional.fieldId];
  const right = field.conditional.value;
  const operator = field.conditional.operator;

  if (operator === 'equals') return String(left ?? '') === String(right ?? '');
  if (operator === 'not_equals') return String(left ?? '') !== String(right ?? '');
  if (operator === 'greater_than') return Number(left) > Number(right);
  if (operator === 'less_than') return Number(left) < Number(right);

  const leftValues = Array.isArray(left) ? left.map((value) => String(value)) : [String(left ?? '')];
  const rightString = String(right ?? '');
  if (operator === 'includes') return leftValues.some((value) => value === rightString);
  if (operator === 'not_includes') return !leftValues.some((value) => value === rightString);
  return true;
}

export default function MedicalFormRenderer({
  definition,
  answers,
  onChange,
  readOnly = false,
}: {
  definition: MedicalFormDefinition;
  answers: Record<string, unknown>;
  onChange?: (fieldId: string, value: unknown) => void;
  readOnly?: boolean;
}) {
  function updateField(field: MedicalFormField, value: unknown) {
    if (readOnly) return;
    onChange?.(field.id, value);
  }

  return (
    <div className="space-y-4">
      {definition.introText ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">{definition.introText}</div>
      ) : null}
      {definition.sections.map((section) => (
        <div key={section.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4">
            <p className="text-base font-semibold text-slate-900">{section.title}</p>
            {section.description ? <p className="mt-1 text-sm text-slate-500">{section.description}</p> : null}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {section.fields.map((field) => {
              if (!isVisible(field, answers)) return null;
              const value = answers[field.id];
              const width = field.layout?.width === 'half' ? 'md:col-span-1' : 'md:col-span-2';
              const commonInputProps = {
                disabled: readOnly,
                required: field.required,
                placeholder: field.placeholder || '',
                className:
                  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 disabled:bg-slate-50 disabled:text-slate-500',
              };

              if (field.type === 'instruction') {
                return (
                  <div key={field.id} className={`${width} rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900`}>
                    <p className="font-medium">{field.label}</p>
                    {field.helpText ? <p className="mt-1 text-blue-800">{field.helpText}</p> : null}
                  </div>
                );
              }

              return (
                <div key={field.id} className={`${width} space-y-2`}>
                  <label className="text-sm font-medium text-slate-700">
                    {field.label}
                    {field.required ? <span className="ml-1 text-rose-600">*</span> : null}
                  </label>
                  {field.helpText ? <p className="text-xs text-slate-500">{field.helpText}</p> : null}

                  {field.type === 'textarea' ? (
                    <textarea
                      {...commonInputProps}
                      rows={4}
                      value={String(value ?? '')}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  ) : null}

                  {field.type === 'text' ? (
                    <input {...commonInputProps} type="text" value={String(value ?? '')} onChange={(event) => updateField(field, event.target.value)} />
                  ) : null}

                  {field.type === 'number' ? (
                    <input
                      {...commonInputProps}
                      type="number"
                      value={value === null || value === undefined ? '' : String(value)}
                      min={field.validation?.min}
                      max={field.validation?.max}
                      step={field.validation?.step ?? 1}
                      onChange={(event) => updateField(field, event.target.value === '' ? null : Number(event.target.value))}
                    />
                  ) : null}

                  {['date', 'datetime', 'time', 'email', 'phone', 'url'].includes(field.type) ? (
                    <input
                      {...commonInputProps}
                      type={field.type === 'datetime' ? 'datetime-local' : field.type === 'phone' ? 'tel' : field.type}
                      value={String(value ?? '')}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  ) : null}

                  {field.type === 'select' ? (
                    <select {...commonInputProps} value={String(value ?? '')} onChange={(event) => updateField(field, event.target.value)}>
                      <option value="">Select...</option>
                      {(field.options || []).map((option) => (
                        <option key={`${field.id}-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : null}

                  {field.type === 'radio' ? (
                    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      {(field.options || []).map((option) => (
                        <label key={`${field.id}-${option.value}`} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name={`radio-${field.id}`}
                            disabled={readOnly}
                            checked={String(value ?? '') === option.value}
                            onChange={() => updateField(field, option.value)}
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  ) : null}

                  {field.type === 'multiselect' ? (
                    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      {(field.options || []).map((option) => {
                        const values = toArrayValue(value);
                        const checked = values.includes(option.value);
                        return (
                          <label key={`${field.id}-${option.value}`} className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              disabled={readOnly}
                              checked={checked}
                              onChange={(event) => {
                                const next = new Set(values);
                                if (event.target.checked) next.add(option.value);
                                else next.delete(option.value);
                                updateField(field, Array.from(next));
                              }}
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                  ) : null}

                  {field.type === 'checkbox' && (field.options || []).length > 0 ? (
                    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      {(field.options || []).map((option) => {
                        const values = toArrayValue(value);
                        const checked = values.includes(option.value);
                        return (
                          <label key={`${field.id}-${option.value}`} className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              disabled={readOnly}
                              checked={checked}
                              onChange={(event) => {
                                const next = new Set(values);
                                if (event.target.checked) next.add(option.value);
                                else next.delete(option.value);
                                updateField(field, Array.from(next));
                              }}
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                  ) : null}

                  {field.type === 'checkbox' && (field.options || []).length === 0 ? (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" disabled={readOnly} checked={Boolean(value)} onChange={(event) => updateField(field, event.target.checked)} />
                      Mark as confirmed
                    </label>
                  ) : null}

                  {field.type === 'switch' ? (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" disabled={readOnly} checked={Boolean(value)} onChange={(event) => updateField(field, event.target.checked)} />
                      Enabled
                    </label>
                  ) : null}

                  {field.type === 'rating' || field.type === 'scale' ? (
                    <div className="space-y-1">
                      <input
                        {...commonInputProps}
                        type="range"
                        min={field.validation?.min ?? 0}
                        max={field.validation?.max ?? 10}
                        step={field.validation?.step ?? 1}
                        value={Number(value ?? field.validation?.min ?? 0)}
                        onChange={(event) => updateField(field, Number(event.target.value))}
                      />
                      <div className="text-xs text-slate-500">Value: {String(value ?? field.validation?.min ?? 0)}</div>
                    </div>
                  ) : null}

                  {field.type === 'signature' ? (
                    <input
                      {...commonInputProps}
                      type="text"
                      placeholder={field.placeholder || 'Type full legal name as signature'}
                      value={String(value ?? '')}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  ) : null}

                  {field.type === 'file' ? (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                      Upload supporting files from the Medical Records page.
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
